import { useDispatch, useSelector } from "react-redux";
import { useCallback, useState, useRef, memo, useMemo } from "react";
import * as THREE from 'three';
import Ceiling from "../Ceiling";
import { DragSession, TwoDMesh, TwoDRoomLayout } from "@/store/builderSlice";
import BuilderSelect from "@/store/builderStoreSelector";
import { useFrame, useThree } from "@react-three/fiber";
import { CSG } from "three-csg-ts";
import { Line, useGLTF } from "@react-three/drei";
import { DimensionalViewType } from "@/utils/builder/builderConstants";
import { DoorOrWindowMesh } from "../Walls";
import { v4 as uuidv4 } from 'uuid';
import React from "react";


export interface ThreeDLayoutWallRendererProps {
    // roomModelLoaded: Function;
    // toggleControls: Function;
    wallMesh: TwoDMesh;
    doorMeshes: DoorOrWindowMesh[],
    windowMeshes: DoorOrWindowMesh[],
}

const ThreeDLayoutWallRenderer: React.FC<ThreeDLayoutWallRendererProps> = (
    { wallMesh, doorMeshes, windowMeshes }) => {
    const dispatch = useDispatch();
    const meshRef = useRef<THREE.Mesh>(null);
    const hiddenMeshRef = useRef<THREE.Mesh>(null);
    const scene = useThree((state) => state.scene);
    const camera = useThree((state) => state.camera);
    const cameraDirRef = useRef(new THREE.Vector3(0, 0, 0));
    const wallHideCount = useRef(0);
    const dragSession: DragSession = BuilderSelect.ui().dragSession;
    const is2DView: boolean = BuilderSelect.ui().dimensionalView === DimensionalViewType.TWO_D;
    const is2DViewRef = useRef(false);
    is2DViewRef.current = is2DView;
    // const [roomBoundingBox, setRoomBoundingBox] = useState(new THREE.Box3());
    // const centerRef = useRef(new THREE.Vector3());
    // const floorGeometryRef = useRef<any>();
    // const floorMeshRef = useRef<THREE.Mesh | undefined>(undefined);
    // const twoDRoomLayout: TwoDRoomLayout = BuilderSelect.twoDRoomLayout();
    // const METER_TO_INCHES_FACTOR = 39.3701;
    const meter_factor = 0.3048;
    const posX = wallMesh.position.x * meter_factor;
    const posZ = wallMesh.position.y * meter_factor;
    const rotY = (Math.abs(wallMesh.rotation)) * (Math.PI / 180);
    const wallWidth = wallMesh.width * meter_factor;
    const wallHeight = 2.5;
    const surfaceId = wallMesh.id;

    // SM Performance Improvements - Oct 9 - as per -> (https://r3f.docs.pmnd.rs/advanced/pitfalls)
    const rayCaster = new THREE.Raycaster();
    const cameraDir = new THREE.Vector3();

    useFrame(() => {
        if (!hiddenMeshRef.current || !meshRef.current || is2DViewRef.current) return;
        
        camera.getWorldDirection(cameraDir);

        // if (cameraDirRef.current && Math.abs(cameraDir.x - cameraDirRef.current.x) < 0.05 &&
        //     Math.abs(cameraDir.y - cameraDirRef.current.y) < 0.05 && Math.abs(cameraDir.z - cameraDirRef.current.z) < 0.05)
        //     return;

        cameraDirRef.current = cameraDir;
        const meshNormal = new THREE.Vector3();
        hiddenMeshRef.current.getWorldDirection(meshNormal);

        const pos = new THREE.Vector3();
        meshRef.current.getWorldPosition(pos);
        const direction = new THREE.Vector3();
        direction.copy(pos).sub(camera.position).normalize();
        let clickMouse = new THREE.Vector2();
        rayCaster.setFromCamera(clickMouse, camera);
        rayCaster.set(camera.position, direction);
        const intersections = rayCaster.intersectObject(scene, true);

        const wallNames: string[] = [];
        let currentMesh: THREE.Mesh | null = null;
        let windowsAndDoorsGroup: THREE.Group | null = null;
        let wallSeperationLinesGroup: THREE.Group | null = null;
        let wallBaseboardGroup: THREE.Group | null = null;
        scene.traverse((child: any) => {
            if (child.userData.type && child.userData.type === surfaceId) {
                currentMesh = child;
            }
            if (child.userData.type && child.userData.type === surfaceId + '_windows_doors') {
                windowsAndDoorsGroup = child;
            }
            if (child.userData.type && child.userData.type === surfaceId + '_wall_seperation_lines') {
                wallSeperationLinesGroup = child;
            }
            if (child.userData.type && child.userData.type === surfaceId + '_wall_baseboard') {
                wallBaseboardGroup = child;
            }
        });
        let hideWall = false;
        if (intersections.length > 0) {
            intersections.forEach(intersection => {
                if (intersection.object.type === 'Mesh' && intersection.object.userData.wtype) {
                    wallNames.push(intersection.object.userData.wtype);
                }
            });
            hideWall = !wallNames.includes(surfaceId);
        }
        if (hideWall) wallHideCount.current = wallHideCount.current + 1;
        else wallHideCount.current = 0;

        if (currentMesh)
            (currentMesh as THREE.Mesh).visible = !(wallHideCount.current > 3);
        if (windowsAndDoorsGroup)
            (windowsAndDoorsGroup as THREE.Group).visible = !(wallHideCount.current > 3);
        if (wallSeperationLinesGroup)
            (wallSeperationLinesGroup as THREE.Group).visible = !(wallHideCount.current > 3);
        if (wallBaseboardGroup)
            (wallBaseboardGroup as THREE.Group).visible = !(wallHideCount.current > 3);
    });
    let initRot = Math.abs(wallMesh.rotation) < 45 ? wallMesh.rotation : (180 - wallMesh.rotation);


    if (surfaceId === 'wall0') {
        initRot = -wallMesh.rotation;
    }
    if (surfaceId === 'wall2') initRot = wallMesh.rotation;

    if (surfaceId === 'wall1') {
        initRot = wallMesh.rotation > 0 ? -wallMesh.rotation : -((wallMesh.rotation + 90) + 90);
    }
    if (surfaceId === 'wall3') {
        initRot = wallMesh.rotation > 0 ? wallMesh.rotation : ((wallMesh.rotation + 90) + 90);
    }

    const planeRotY = initRot * (Math.PI / 180);
    const color = surfaceId === 'wall0' ? 'blue' : surfaceId === 'wall2' ? 'yellow' : surfaceId === 'wall1' ? 'orange' : 'red';

    console.log('canvas-wall :: ', surfaceId, wallMesh.rotation, initRot);
    const rotX = surfaceId === 'wall3' || surfaceId === 'wall2' ? Math.PI : 0;

    if (is2DViewRef.current) {
        scene.traverse((child: any) => {
            if (child.userData && child.userData.type && child.userData.type === surfaceId) {
                (child as THREE.Group).visible = true;
            }
            if (child.userData && child.userData.type && child.userData.type === surfaceId + '_windows_doors') {
                (child as THREE.Group).visible = true;
            }
            if (child.userData && child.userData.type && (child.userData.type === surfaceId + '_wall_seperation_lines')) {
                (child as THREE.Group).visible = false;
            }
            if (child.userData && child.userData.type && child.userData.type === surfaceId + '_wall_baseboard') {
                (child as THREE.Group).visible = false;
            }
        });
    }

    const createHoleOnWall = (mainMesh: THREE.Mesh, holeMesh: TwoDMesh, wallBoundingBox: THREE.Box3) => {
        //holeMesh.geometry.computeBoundingBox();
        //const holeBoundingBox = new THREE.Box3().setFromObject(holeMesh);

        // let outerWidth = wallBoundingBox.max.z - wallBoundingBox.min.z;
        // if (outerWidth < 0.8) {
        //     outerWidth = wallBoundingBox.max.x - wallBoundingBox.min.x;
        // }
        // let wallWidth = width;
        // //const outerDepth = wallBoundingBox.max.y - wallBoundingBox.min.y;
        // const wallHeight = 2.5;
        // //let holeWidth = (holeBoundingBox.max.z - holeBoundingBox.min.z);
        // let holeWidth = 2;
        // let deltaX = wallBoundingBox.min.z - holeBoundingBox.min.z;
        // let sign = wallBoundingBox.min.x < 0 ? 1 : -1;
        // if (holeWidth < 0.3) {
        //     holeWidth = (holeBoundingBox.max.x - holeBoundingBox.min.x);
        //     deltaX = wallBoundingBox.min.x - holeBoundingBox.min.x;
        //     sign = wallBoundingBox.min.z < 0 ? -1 : 1;
        // }
        // holeWidth = holeWidth / wallWidth;
        //const holeHeight = (holeBoundingBox.max.y - holeBoundingBox.min.y) / wallHeight;
        // const posX = sign * ((wallWidth / 2) - (holeWidth / 2)) + sign * (deltaX / wallWidth);
        // const posZ = ((wallHeight / 2) - (holeHeight / 2)) + ((wallBoundingBox.min.y - holeBoundingBox.min.y) / wallHeight);

        const holeWidth = holeMesh.width * meter_factor;
        const holeHeight = holeMesh.type === 'door' ? 2 : 1.25;
        let posX = (holeMesh.position.x - wallMesh.position.x) * meter_factor;
        if (wallMesh.id === 'wall1' || wallMesh.id === 'wall3')
            posX = (holeMesh.position.y - wallMesh.position.y) * meter_factor;
        let posY = holeMesh.type === 'door' ? wallHeight / 2 - holeHeight / 2 : 0;
        if (wallMesh.id === 'wall0' || wallMesh.id === 'wall1')
            posY = -posY;
        const holeBox = new THREE.Mesh(new THREE.BoxGeometry(holeWidth, holeHeight, 1));
        holeBox.position.x = posX;
        holeBox.position.y = posY;
        holeBox.updateMatrix();

        console.log('canvas- hole - ', wallMesh.id, wallWidth, holeWidth, posX);

        // Perform CSG operations
        const resultMesh = CSG.subtract(mainMesh, holeBox);
        resultMesh.updateMatrix();
        resultMesh.geometry.computeBoundingBox();
        resultMesh.geometry.computeVertexNormals();
        return resultMesh;
    }

    const wallMeshWithDoorsAndWindows = useMemo(() => {

        const material = new THREE.MeshStandardMaterial();
        material.color = new THREE.Color('gray');
        material.roughness = 100;
        material.metalness = 0.75;
        const wallGeometry = new THREE.BoxGeometry(wallWidth, wallHeight, 0.15);
        const wallBox = new THREE.Mesh(
            wallGeometry,
            material
        );
        // wallBox.position.x = posX;
        // wallBox.position.y = 0;
        // wallBox.position.z = posZ;

        // wallBox.rotation.x = rotX;
        // wallBox.rotation.y = planeRotY;
        // wallBox.rotation.z = 0;
        wallBox.updateMatrix();
        const wallBoundingBox = new THREE.Box3().setFromObject(wallBox!);
        let resultMesh: THREE.Mesh = wallBox;


        const doorGeometry = new THREE.BoxGeometry(1, 1, 0.15);
        const doorMesh = new THREE.Mesh(
            doorGeometry,
            material
        );
        doorMesh.position.x = posX;
        doorMesh.position.y = 0;
        doorMesh.position.z = posZ;

        doorMesh.rotation.x = rotX;
        doorMesh.rotation.y = planeRotY;
        doorMesh.rotation.z = 0;
        doorMesh.updateMatrix();

        wallMesh.children.forEach(doorMesh => {
            resultMesh = createHoleOnWall(resultMesh, doorMesh, wallBoundingBox);
        });

        // if (windowMeshes.length > 0) {
        //     windowMeshes.forEach(windowMesh => {
        //         resultMesh = createHoleOnWall(resultMesh, windowMesh.mesh, wallBoundingBox);
        //     });
        // }
        return resultMesh;

    }, [wallMesh]);

    const getWindowOrDoorMesh = (meshObj: DoorOrWindowMesh) => {

        const mesh = meshObj.mesh as TwoDMesh;
        const model = meshObj.asset.model;
        const modelBox = new THREE.Box3().setFromObject(model);
        const modelSize = new THREE.Vector3();
        modelBox.getSize(modelSize);
        const color = mesh.type === 'door' ? '#73C6B6' : '#2471A3';
        console.log('debug-canvas- getWindowOrDoorMesh : ', mesh.id, modelSize);

        // const meshBox = new THREE.Box3().setFromObject(mesh);
        // const meshSize = new THREE.Vector3();
        // meshBox.getSize(meshSize);
        const holeHeight = mesh.type === 'door' ? 2 : 1.25;

        let scaleX = mesh.width * meter_factor / modelSize.x;
        //const scaleZ = meshSize.z / (modelSize.y > 0.5 ? modelSize.y : modelSize.z);
        let scaleY = holeHeight / modelSize.y;
        if (is2DView) {
            scaleX = scaleY = 1;
        }

        const posX = mesh.position.x * meter_factor;
        const posY = mesh.type === 'door' ? wallHeight / 2 - holeHeight / 2 : 0;
        const posZ = mesh.position.y * meter_factor;
        const depthOffsetX = wallMesh.id === 'wall0' || wallMesh.id === 'wall2' ? 0 : 0;
        const depthOffsetZ = wallMesh.id === 'wall0' || wallMesh.id === 'wall2' ? 0 : 0;
        const yOffset = is2DView ? (mesh.type === 'door' ? 0.4 : 0.15) : 0;
        return (<group
            position={[posX - depthOffsetX, -posY + yOffset, posZ - depthOffsetZ]}
            rotation={[rotX, planeRotY, 0]}
            scale={[scaleX, scaleY, 1]}
        >
            {!is2DView ?
                (<primitive
                    rotation={[0, 0, 0]}
                    position={[0, 0, 0]}
                    object={model!.clone()} />)
                :
                (<mesh rotation={[0, 0, 0]}
                    position={[0, 0, 0]}
                    name={meshObj.mesh.type}
                >
                    <boxGeometry attach="geometry" args={[mesh.width * meter_factor, 0.1, 0.2]} />
                    <meshStandardMaterial attach="material" color={color} />
                </mesh>)
            }
        </group>);
    }

    const baseBoardPositionY = wallMesh.id === 'wall0' || wallMesh.id === 'wall1' ? -wallHeight / 2 : wallHeight / 2;
    const wallScaleY = is2DView ? 0.1 : 1;
    return <>
        <group
            position={[posX, 0, posZ]}
            rotation={[rotX, planeRotY, 0]}
        >
            <mesh
                geometry={wallMeshWithDoorsAndWindows.geometry}
                userData={{ type: surfaceId }}
                ref={meshRef}
                name={surfaceId}
                scale={[1, wallScaleY, 1]}
            >
                {/* <boxGeometry attach="geometry" args={[width, wallHeight, 0.15]} /> */}
                <meshStandardMaterial attach="material" color={wallMesh.color || '#A3937E'} />
            </mesh>
            <mesh
                visible={true}
                ref={hiddenMeshRef}
                userData={{ wtype: surfaceId, wallType: 'hidden' }}
                name={surfaceId + '_hidden'}
            >
                <planeGeometry attach="geometry" args={[wallWidth, wallHeight]} />
                <meshStandardMaterial color={color} transparent={true} opacity={0} side={THREE.FrontSide} />
            </mesh>
        </group>
        <group
            position={[posX, 0, posZ]}
            rotation={[rotX, planeRotY, 0]}
            userData={{ type: surfaceId + '_wall_seperation_lines' }}
        >
            <Line
                points={[[-wallWidth / 2 + 0.1, -wallHeight / 2, 0.1], [-wallWidth / 2 + 0.1, wallHeight / 2, 0.1]]}
                color="white"
                lineWidth={1.5}
            />
            <Line
                points={[[wallWidth / 2 - 0.1, -wallHeight / 2, 0.1], [wallWidth / 2 - 0.1, wallHeight / 2, 0.1]]}
                color="white"
                lineWidth={1.5}
            />

        </group>
        <group
            position={[posX, 0, posZ]}
            rotation={[rotX, planeRotY, 0]}
            userData={{ type: surfaceId + '_wall_baseboard' }}
        >
            <mesh position={[0, baseBoardPositionY, 0.1]}
                name={surfaceId + '_wall_baseboard'}
            >
                <boxGeometry attach="geometry" args={[wallWidth, 0.15, 0.05]} />
                <meshStandardMaterial attach="material" color={'white'} />
            </mesh>
        </group>
        <group
            position={[posX, 0, posZ]}
            rotation={[rotX, planeRotY, 0]}
            visible={dragSession.isActive}
        >
            <mesh position={[0, baseBoardPositionY, 0.125]}
                name={surfaceId + '_wall_baseboard_green'}
            >
                <boxGeometry attach="geometry" args={[wallWidth, 0.025, 0.025]} />
                <meshStandardMaterial attach="material" color={'green'} />
            </mesh>
        </group>
        <group userData={{ type: surfaceId + '_windows_doors' }}>
            {doorMeshes.map((doorMesh) => (
                <React.Fragment key={uuidv4()}> 
                    {getWindowOrDoorMesh(doorMesh)}
                </React.Fragment>)
            )}
            {windowMeshes.map((windowMesh) => (
                <React.Fragment key={uuidv4()}> 
                    {getWindowOrDoorMesh(windowMesh)}
                </React.Fragment>)
            )}
        </group>

    </>
}

export default memo(ThreeDLayoutWallRenderer);